"use client"

import * as React from "react"
import { AlertCircle, KeyRound, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { agentsService } from "@/services/agentsService"
import type { ProviderCredentialProvider, ProviderCredentialStatus } from "@/types/agent"

const PROVIDERS: Array<{
  id: ProviderCredentialProvider
  labelKey: string
  descriptionKey: string
}> = [
  {
    id: "openai",
    labelKey: "settings.api_keys.providers.openai.label",
    descriptionKey: "settings.api_keys.providers.openai.description",
  },
  {
    id: "anthropic",
    labelKey: "settings.api_keys.providers.anthropic.label",
    descriptionKey: "settings.api_keys.providers.anthropic.description",
  },
  {
    id: "google",
    labelKey: "settings.api_keys.providers.google.label",
    descriptionKey: "settings.api_keys.providers.google.description",
  },
]

function getStatusLabel(status: ProviderCredentialStatus | undefined, t: (key: string) => string): string {
  if (!status) return t("settings.api_keys.status.missing")
  if (status.has_stored_key) return t("settings.api_keys.status.stored_key")
  if (status.using_env_fallback) return t("settings.api_keys.status.environment_fallback")
  return t("settings.api_keys.status.missing")
}

function getStatusVariant(status?: ProviderCredentialStatus): "default" | "secondary" | "outline" {
  if (!status) return "outline"
  if (status.has_stored_key) return "default"
  if (status.using_env_fallback) return "secondary"
  return "outline"
}

function getPlaceholder(status: ProviderCredentialStatus | undefined, t: (key: string, values?: Record<string, string>) => string): string {
  if (!status) return t("settings.api_keys.placeholders.input")
  if (status.has_stored_key && status.masked_value) {
    return t("settings.api_keys.placeholders.current", { masked: status.masked_value })
  }
  if (status.using_env_fallback) {
    return t("settings.api_keys.placeholders.using_env_fallback")
  }
  return t("settings.api_keys.placeholders.no_key_configured")
}

export function ApiKeysSettings() {
  const { t } = useTranslation()
  const [items, setItems] = React.useState<ProviderCredentialStatus[]>([])
  const [values, setValues] = React.useState<Record<ProviderCredentialProvider, string>>({
    openai: "",
    anthropic: "",
    google: "",
  })
  const [loading, setLoading] = React.useState(true)
  const [busyProvider, setBusyProvider] = React.useState<ProviderCredentialProvider | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)

  const loadStatuses = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const nextItems = await agentsService.listProviderCredentials()
      setItems(nextItems)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t("settings.api_keys.errors.load_statuses"))
    } finally {
      setLoading(false)
    }
  }, [t])

  React.useEffect(() => {
    void loadStatuses()
  }, [loadStatuses])

  React.useEffect(() => {
    if (!success) return
    const timeout = window.setTimeout(() => setSuccess(null), 3500)
    return () => window.clearTimeout(timeout)
  }, [success])

  const itemsByProvider = React.useMemo(() => {
    return Object.fromEntries(items.map((item) => [item.provider, item])) as Partial<
      Record<ProviderCredentialProvider, ProviderCredentialStatus>
    >
  }, [items])

  const handleSave = async (provider: ProviderCredentialProvider) => {
    const apiKey = values[provider]?.trim()
    if (!apiKey) return

    try {
      setBusyProvider(provider)
      setError(null)
      await agentsService.updateProviderCredential(provider, apiKey)
      setValues((prev) => ({ ...prev, [provider]: "" }))
      const providerName = t(PROVIDERS.find((item) => item.id === provider)?.labelKey || provider)
      setSuccess(t("settings.api_keys.success.updated", { provider: providerName }))
      await loadStatuses()
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t("settings.api_keys.errors.save_credential"))
    } finally {
      setBusyProvider(null)
    }
  }

  const handleClear = async (provider: ProviderCredentialProvider) => {
    try {
      setBusyProvider(provider)
      setError(null)
      await agentsService.deleteProviderCredential(provider)
      setValues((prev) => ({ ...prev, [provider]: "" }))
      const providerName = t(PROVIDERS.find((item) => item.id === provider)?.labelKey || provider)
      setSuccess(t("settings.api_keys.success.removed", { provider: providerName }))
      await loadStatuses()
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : t("settings.api_keys.errors.clear_credential"))
    } finally {
      setBusyProvider(null)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("settings.api_keys.title")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("settings.api_keys.description")}
        </p>
      </div>

      {success && (
        <Alert>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("settings.api_keys.loading")}
        </div>
      ) : (
        <div className="space-y-4">
          {PROVIDERS.map((provider) => {
            const status = itemsByProvider[provider.id]
            const isBusy = busyProvider === provider.id

            return (
              <Card key={provider.id}>
                <CardHeader>
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-muted-foreground" />
                        {t(provider.labelKey)}
                      </CardTitle>
                      <CardDescription className="mt-1">{t(provider.descriptionKey)}</CardDescription>
                    </div>
                    <Badge variant={getStatusVariant(status)}>{getStatusLabel(status, t)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`provider-key-${provider.id}`}>{t("settings.api_keys.api_key_label")}</Label>
                    <Input
                      id={`provider-key-${provider.id}`}
                      type="password"
                      value={values[provider.id]}
                      onChange={(event) =>
                        setValues((prev) => ({
                          ...prev,
                          [provider.id]: event.target.value,
                        }))
                      }
                      placeholder={getPlaceholder(status, t)}
                      autoComplete="off"
                      disabled={isBusy}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {status?.has_stored_key && status.updated_at ? (
                      <span>
                        {t("settings.api_keys.status_messages.stored_key_updated", {
                          date: new Date(status.updated_at).toLocaleString(),
                        })}
                      </span>
                    ) : null}
                    {status?.using_env_fallback ? (
                      <span>{t("settings.api_keys.status_messages.using_env_fallback")}</span>
                    ) : null}
                    {!status?.has_stored_key && !status?.using_env_fallback ? (
                      <span>{t("settings.api_keys.status_messages.no_credential")}</span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => void handleSave(provider.id)} disabled={!values[provider.id].trim() || isBusy}>
                      {isBusy ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t("settings.api_keys.actions.saving")}
                        </>
                      ) : (
                        t("settings.api_keys.actions.save_key")
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => void handleClear(provider.id)}
                      disabled={!status?.has_stored_key || isBusy}
                    >
                      {t("settings.api_keys.actions.clear_stored_key")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
